import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LeavesCanvas from "@/components/LeavesCanvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Leaf, ShieldCheck, KeyRound, QrCode, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [preToken, setPreToken] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [totpQr, setTotpQr] = useState("");
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const processStage = (data) => {
    setPreToken(data.pre_token);
    if (data.next === "change_password") setStep("change_password");
    else if (data.next === "setup_totp") {
      setTotpSecret(data.totp_secret);
      setTotpQr(data.totp_qr);
      setStep("setup_totp");
    } else setStep("verify_totp");
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      processStage(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const doChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", { pre_token: preToken, new_password: newPassword });
      toast.success("Password updated");
      processStage(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const doConfirmTotp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/setup-totp/confirm", { pre_token: preToken, code });
      setRecovery(data.recovery_codes || []);
      setUser(data.user);
      setStep("recovery");
    } catch (err) {
      toast.error(apiError(err));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const doVerifyTotp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-totp", { pre_token: preToken, code });
      setUser(data.user);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center p-4">
      <LeavesCanvas enabled />
      <div className="glass-strong relative z-10 w-full max-w-md animate-fade-up rounded-3xl p-8">
        <span className="spec-shine" />
        <div className="mb-7 flex flex-col items-center text-center">
          <div className="glossy-emerald relative mb-4 flex h-16 w-16 items-center justify-center rounded-3xl">
            <Leaf className="h-8 w-8" />
            <span className="spec-shine" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">GSuite Ninja</h1>
          <p className="mt-1 text-sm text-slate-500">Multi-domain Workspace command center</p>
        </div>

        {step === "login" && (
          <form onSubmit={doLogin} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required placeholder="admin@gsuitecentral.com" data-testid="login-email-input"
                className="h-11 rounded-xl bg-white/70" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required placeholder="••••••••" data-testid="login-password-input"
                className="h-11 rounded-xl bg-white/70" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="glossy-emerald relative h-11 w-full rounded-xl text-sm font-semibold hover:brightness-105">
              <span className="spec-shine" />
              {loading ? "Signing in…" : "Continue"}
            </Button>
            <p className="pt-2 text-center text-xs text-slate-400">Protected by mandatory 2-Step Verification</p>
          </form>
        )}

        {step === "change_password" && (
          <form onSubmit={doChangePassword} className="space-y-4">
            <div className="mb-2 flex items-center gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-700">
              <KeyRound className="h-4 w-4" /> Set a new password for your account
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="np">New password</Label>
              <Input id="np" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                required minLength={8} placeholder="At least 8 characters" data-testid="change-password-input"
                className="h-11 rounded-xl bg-white/70" />
            </div>
            <Button type="submit" disabled={loading} data-testid="change-password-submit"
              className="glossy-emerald relative h-11 w-full rounded-xl font-semibold">
              <span className="spec-shine" />{loading ? "Saving…" : "Update password"}
            </Button>
          </form>
        )}

        {step === "setup_totp" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">
              <QrCode className="h-4 w-4" /> Scan with Google Authenticator / Authy
            </div>
            {totpQr && (
              <div className="flex justify-center">
                <img src={totpQr} alt="TOTP QR" className="h-44 w-44 rounded-2xl border border-white/80 bg-white p-2 shadow" data-testid="totp-qr-image" />
              </div>
            )}
            <div className="flex items-center justify-between gap-2 rounded-xl bg-white/70 px-3 py-2 font-mono text-xs">
              <span className="truncate" data-testid="totp-secret">{totpSecret}</span>
              <button type="button" onClick={() => { try { navigator.clipboard.writeText(totpSecret); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {} }}
                className="text-emerald-600" data-testid="copy-secret-button">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex flex-col items-center gap-3">
              <Label className="self-start">Enter the 6-digit code</Label>
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="totp-setup-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button onClick={doConfirmTotp} disabled={loading || code.length !== 6} data-testid="totp-setup-confirm"
              className="glossy-emerald relative h-11 w-full rounded-xl font-semibold">
              <span className="spec-shine" />{loading ? "Verifying…" : "Verify & Enable 2FA"}
            </Button>
          </div>
        )}

        {step === "verify_totp" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">
              <ShieldCheck className="h-4 w-4" /> Enter the code from your authenticator app
            </div>
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="totp-verify-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button onClick={doVerifyTotp} disabled={loading || code.length !== 6} data-testid="totp-verify-submit"
              className="glossy-emerald relative h-11 w-full rounded-xl font-semibold">
              <span className="spec-shine" />{loading ? "Verifying…" : "Verify & Sign in"}
            </Button>
          </div>
        )}

        {step === "recovery" && (
          <div className="space-y-4">
            <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-700">
              Save these one-time recovery codes somewhere safe. They won't be shown again.
            </div>
            <div className="grid grid-cols-2 gap-2 rounded-2xl bg-white/70 p-4 font-mono text-sm" data-testid="recovery-codes">
              {recovery.map((c) => <span key={c} className="rounded-lg bg-emerald-50 px-2 py-1 text-center text-emerald-800">{c}</span>)}
            </div>
            <Button onClick={() => navigate("/")} data-testid="recovery-continue"
              className="glossy-emerald relative h-11 w-full rounded-xl font-semibold">
              <span className="spec-shine" />Enter Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
